import React, { useRef, useState } from "react";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import UploadIcon from "@mui/icons-material/CloudUpload";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import "./FileUpload.css";

const MAX_FILES = 10000;

const FileUpload = ({ onUpload, isAdmin }) => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef();

  const processFiles = (newFiles) => {
    setError("");
    const pdfFiles = newFiles.filter((file) => file.type === "application/pdf");
    if (pdfFiles.length !== newFiles.length) {
      setError("Only PDF files are allowed.");
      return;
    }
    setFiles((prev) => [...prev, ...pdfFiles]);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    processFiles(newFiles);
    inputRef.current.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const newFiles = Array.from(e.dataTransfer.files);
    processFiles(newFiles);
  };

  const handleRemove = (idx) => {
    setFiles((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      return updated;
    });
  };

  const handleUpload = () => {
    if (files.length === 0) {
      setError("No files selected.");
      return;
    }
    if (onUpload) onUpload(files);
    setFiles([]);
  };

  const openFileDialog = () => {
    if (inputRef.current) inputRef.current.click();
  };

  return (
    <div
      className={`file-upload-container ${isDragging ? "drag-over" : ""}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        type="file"
        multiple
        accept="application/pdf"
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        id="file-upload-input"
      />
      {isAdmin && (
        <Paper
          elevation={0}
          className="file-upload-paper"
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            minHeight: 56,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            px: 2,
            py: 1,
          }}
        >
          <Tooltip title="Select PDF files">
            <IconButton
              color="primary"
              component="span"
              onClick={openFileDialog}
              size="large"
              sx={{ mr: 1 }}
              aria-label="Select PDF files"
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            className="file-upload-chip-stack"
            style={{ flex: 1, minWidth: 0 }}
          >
            {files.length === 0 ? (
              <div className="file-upload-placeholder">
                Drag and drop files here
              </div>
            ) : (
              files.map((file, idx) => (
                <Chip
                  key={idx}
                  label={file.name}
                  onDelete={() => handleRemove(idx)}
                  color="default"
                  variant="outlined"
                  sx={{ mb: 0.5, maxWidth: 180 }}
                />
              ))
            )}
          </Stack>
          <Button
            variant="contained"
            color="primary"
            endIcon={<UploadIcon />}
            onClick={handleUpload}
            disabled={files.length === 0}
            className="file-upload-upload-btn"
            sx={{ ml: 2, minWidth: 120 }}
          >
            Upload
          </Button>
        </Paper>
      )}
      {error && <div className="file-upload-error">{error}</div>}
    </div>
  );
};

export default FileUpload;
